import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TimelineCard, Card } from '../ui/Card';
import { Avatar } from '../ui/Avatar';
import { CountdownBadge, OccasionBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { EmptyTimeline } from '../ui/EmptyState';
import { SkeletonDashboard, SkeletonTimelineCard, SkeletonList } from '../ui/Skeleton';

/**
 * TimelineView Component
 *
 * The main dashboard showing upcoming occasions.
 * Premium design with beautiful animations and micro-interactions.
 */

// CSS Animations
const animationStyles = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes pulse-subtle {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }

  @keyframes gradient-shift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
  }

  .animate-fade-in-up {
    animation: fadeInUp 0.5s ease-out forwards;
  }

  .animate-fade-in {
    animation: fadeIn 0.3s ease-out forwards;
  }

  .animate-scale-in {
    animation: scaleIn 0.3s ease-out forwards;
  }

  .animate-slide-in-right {
    animation: slideInRight 0.4s ease-out forwards;
  }

  .animate-pulse-subtle {
    animation: pulse-subtle 2s ease-in-out infinite;
  }

  .animate-gradient {
    background-size: 200% 200%;
    animation: gradient-shift 3s ease infinite;
  }

  .animate-float {
    animation: float 3s ease-in-out infinite;
  }

  .stagger-1 { animation-delay: 0.1s; }
  .stagger-2 { animation-delay: 0.2s; }
  .stagger-3 { animation-delay: 0.3s; }
  .stagger-4 { animation-delay: 0.4s; }
  .stagger-5 { animation-delay: 0.5s; }
`;

// Inject animation styles
if (typeof document !== 'undefined' && !document.getElementById('timeline-animation-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'timeline-animation-styles';
  styleElement.textContent = animationStyles;
  document.head.appendChild(styleElement);
}

/**
 * Get time of day greeting
 */
function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/**
 * Get greeting emoji based on time
 */
function getGreetingEmoji() {
  const hour = new Date().getHours();
  if (hour < 6) return '🌙';
  if (hour < 12) return '☀️';
  if (hour < 18) return '🌤️';
  if (hour < 21) return '🌅';
  return '🌙';
}

/**
 * Calculate days until a date
 */
function getDaysUntil(dateString) {
  if (!dateString) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const occasionDate = new Date(dateString);
  occasionDate.setFullYear(today.getFullYear());

  if (occasionDate < today) {
    occasionDate.setFullYear(today.getFullYear() + 1);
  }

  const diffTime = occasionDate - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Format date for display
 */
function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Get urgency color scheme
 */
function getUrgencyColors(daysUntil) {
  if (daysUntil === null) return { bg: 'from-slate-400 to-slate-500', text: 'text-slate-600', border: 'border-slate-200' };
  if (daysUntil <= 3) return { bg: 'from-red-500 to-rose-600', text: 'text-red-600', border: 'border-red-200', pulse: true };
  if (daysUntil <= 7) return { bg: 'from-coral-500 to-rose-500', text: 'text-coral-600', border: 'border-coral-200' };
  if (daysUntil <= 14) return { bg: 'from-amber-400 to-orange-500', text: 'text-amber-600', border: 'border-amber-200' };
  if (daysUntil <= 30) return { bg: 'from-gold-400 to-amber-500', text: 'text-gold-600', border: 'border-gold-200' };
  return { bg: 'from-emerald-400 to-teal-500', text: 'text-emerald-600', border: 'border-emerald-200' };
}

/**
 * Premium countdown badge with urgency styling
 */
function PremiumCountdownBadge({ days }) {
  const colors = getUrgencyColors(days);

  const getLabel = () => {
    if (days === 0) return 'Today!';
    if (days === 1) return 'Tomorrow!';
    if (days <= 7) return `${days} days`;
    if (days <= 30) return `${days} days`;
    return `${days} days`;
  };

  return (
    <div
      className={`
        inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
        bg-gradient-to-r ${colors.bg} text-white
        font-semibold text-sm shadow-lg
        ${colors.pulse ? 'animate-pulse-subtle' : ''}
      `}
    >
      {days <= 7 && <span className="text-base">⏰</span>}
      {getLabel()}
    </div>
  );
}

/**
 * UpcomingOccasionCard - Featured hero card for most urgent occasion
 */
export function UpcomingOccasionCard({
  occasion,
  onFindGift,
  onViewPerson,
  isAdmin = false,
  className = '',
}) {
  const daysUntil = getDaysUntil(occasion.date);
  const colors = getUrgencyColors(daysUntil);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onClick={() => onViewPerson?.(occasion)}
      className={`
        relative overflow-hidden rounded-3xl
        bg-gradient-to-br from-white via-white to-coral-50
        shadow-xl border border-slate-100
        transition-all duration-300 ease-out cursor-pointer
        ${isHovered ? 'shadow-2xl -translate-y-1 border-coral-200' : ''}
        ${className}
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-coral-100/50 to-transparent rounded-full -translate-y-20 translate-x-20" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-gold-100/40 to-transparent rounded-full translate-y-16 -translate-x-16" />

      <div className="relative p-6">
        {/* Urgency label */}
        <div className="flex items-center gap-2 mb-4">
          <span className={`text-xs font-bold uppercase tracking-wider ${colors.text}`}>
            {daysUntil <= 7 ? '🔥 Coming up soon' : '📅 Upcoming'}
          </span>
        </div>

        <div className="flex items-start gap-4">
          {/* Premium Avatar */}
          <div
            className={`
              relative group
              transition-transform duration-300
              ${isHovered ? 'scale-105' : ''}
            `}
          >
            <div className={`
              w-16 h-16 rounded-2xl
              bg-gradient-to-br ${colors.bg}
              flex items-center justify-center
              text-white text-2xl font-bold
              shadow-lg group-hover:shadow-xl
              transition-all duration-300
            `}>
              {occasion.recipientName?.charAt(0).toUpperCase()}
            </div>
            {/* Online indicator style badge */}
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white shadow-md flex items-center justify-center">
              <span className="text-sm">{occasion.occasion === 'birthday' ? '🎂' : '🎁'}</span>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-xl font-bold text-slate-900 leading-tight">
                  {occasion.recipientName}'s {occasion.occasion || 'Birthday'}
                </h3>
                <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {formatDate(occasion.date)}
                </p>
              </div>
              <PremiumCountdownBadge days={daysUntil} />
            </div>

            {/* Relationship badge */}
            {occasion.relationship && (
              <div className="mt-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-sm font-medium">
                  {occasion.relationship === 'family' && '👨‍👩‍👧'}
                  {occasion.relationship === 'partner' && '💑'}
                  {occasion.relationship === 'friend' && '👯'}
                  {occasion.relationship === 'colleague' && '💼'}
                  <span className="ml-1.5 capitalize">{occasion.relationship}</span>
                </span>
              </div>
            )}

            {/* Last gift context */}
            {occasion.lastGift && (
              <div className="mt-3 flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-xl">
                <span className="text-lg">🎁</span>
                <span>
                  <span className="text-slate-400">Last time:</span> {occasion.lastGift}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Action button - Admin only */}
        {isAdmin && (
          <div className="mt-5">
            <button
              onClick={() => onFindGift?.(occasion)}
              className={`
                w-full py-3.5 px-6 rounded-2xl
                bg-gradient-to-r ${colors.bg}
                text-white font-semibold
                flex items-center justify-center gap-2
                shadow-lg hover:shadow-xl
                transition-all duration-300
                hover:scale-[1.02] active:scale-[0.98]
              `}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Find the perfect gift
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * UpcomingOccasionRow - Compact card for less urgent occasions
 */
export function UpcomingOccasionRow({
  occasion,
  onClick,
  index = 0,
  className = '',
}) {
  const daysUntil = getDaysUntil(occasion.date);
  const colors = getUrgencyColors(daysUntil);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={() => onClick?.(occasion)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        flex items-center gap-4 p-4 w-full text-left
        bg-white rounded-2xl
        border-2 ${isHovered ? colors.border : 'border-slate-100'}
        shadow-sm hover:shadow-lg
        transition-all duration-300 ease-out
        ${isHovered ? '-translate-y-1' : ''}
        animate-fade-in-up
        ${className}
      `.trim().replace(/\s+/g, ' ')}
    >
      {/* Avatar with gradient */}
      <div className={`
        w-12 h-12 rounded-xl
        bg-gradient-to-br ${colors.bg}
        flex items-center justify-center
        text-white font-bold text-lg
        shadow-md
        transition-transform duration-300
        ${isHovered ? 'scale-110 rotate-3' : ''}
      `}>
        {occasion.recipientName?.charAt(0).toUpperCase()}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-800 truncate">
          {occasion.recipientName}
        </p>
        <p className="text-sm text-slate-500 flex items-center gap-1">
          <span className="capitalize">{occasion.occasion || 'Birthday'}</span>
          <span className="text-slate-300">•</span>
          <span>{new Date(occasion.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
        </p>
      </div>

      {/* Countdown */}
      <div className="flex items-center gap-2">
        <PremiumCountdownBadge days={daysUntil} />
        <svg
          className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${isHovered ? 'translate-x-1' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </button>
  );
}

/**
 * TimelineView - Main dashboard component
 */
export function TimelineView({
  userName = '',
  upcomingOccasions = [],
  recentGifts = [],
  loading = false,
  onAddPerson,
  onFindGift,
  onViewPerson,
  onViewGift,
  isAdmin = false,
  className = '',
}) {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Sort occasions by date
  const sortedOccasions = [...upcomingOccasions].sort((a, b) => {
    const daysA = getDaysUntil(a.date);
    const daysB = getDaysUntil(b.date);
    return (daysA || 999) - (daysB || 999);
  });

  // Featured (most urgent) and upcoming
  const featured = sortedOccasions[0];
  const upcoming = sortedOccasions.slice(1, 5);

  if (loading) {
    return <SkeletonDashboard />;
  }

  return (
    <div className={`px-4 py-6 max-w-2xl mx-auto ${className}`}>
      {/* Premium Greeting */}
      <div className={`mb-8 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} transition-all duration-500`}>
        <div className="flex items-center gap-3 mb-1">
          <span className="text-3xl animate-float">{getGreetingEmoji()}</span>
          <h1 className="text-3xl font-bold text-slate-900">
            {getGreeting()}{userName ? `, ${userName}` : ''}!
          </h1>
        </div>
        {sortedOccasions.length > 0 ? (
          <p className="text-slate-500 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-coral-50 text-coral-600 rounded-full text-sm font-medium">
              <span>📅</span> {sortedOccasions.length} upcoming
            </span>
            {recentGifts.filter(g => g.giftGiven).length > 0 && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-600 rounded-full text-sm font-medium">
                <span>🎁</span> {recentGifts.filter(g => g.giftGiven).length} sent
              </span>
            )}
          </p>
        ) : (
          <p className="text-slate-500">
            Start adding people to never miss a special moment
          </p>
        )}
      </div>

      {/* Empty state */}
      {sortedOccasions.length === 0 && (
        <div className="animate-scale-in">
          <EmptyTimeline onAdd={onAddPerson} />
        </div>
      )}

      {/* Featured upcoming occasion */}
      {featured && (
        <div className="mb-8 animate-fade-in-up">
          <UpcomingOccasionCard
            occasion={featured}
            onFindGift={onFindGift}
            onViewPerson={onViewPerson}
            isAdmin={isAdmin}
          />
        </div>
      )}

      {/* More upcoming */}
      {upcoming.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2 animate-fade-in">
            <span className="w-8 h-0.5 bg-gradient-to-r from-coral-400 to-transparent rounded-full" />
            Coming up
          </h2>
          <div className="space-y-3">
            {upcoming.map((occasion, index) => (
              <UpcomingOccasionRow
                key={occasion.id}
                occasion={occasion}
                onClick={onViewPerson}
                index={index}
              />
            ))}
          </div>

          {sortedOccasions.length > 5 && (
            <button
              onClick={() => navigate('/calendar')}
              className="w-full mt-4 py-3 text-sm text-coral-600 hover:text-coral-700 font-semibold bg-coral-50 hover:bg-coral-100 rounded-xl transition-all flex items-center justify-center gap-2"
            >
              View all {sortedOccasions.length} occasions
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          )}
        </div>
      )}

    </div>
  );
}

export default TimelineView;
